/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The permission grid: object groups, each expandable to per-object overrides.
 *
 * Built on Box/Grid rather than MUI Table on purpose. The app's theme forces
 * JetBrains Mono onto every MuiTableCell body cell (with !important, for tabular
 * data), which is right for a records list and wrong for a settings form -- it
 * rendered group names in a typewriter face that matched nothing else in the
 * product.
 *
 * Access is a segmented control rather than a dropdown. There are only four
 * values, they are ordered, and an admin comparing rows wants to see the whole
 * scale at a glance instead of opening 15 menus to find out what is set.
 */

import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandMore,
  LockOutlined,
  RestartAlt,
  TuneRounded,
} from '@mui/icons-material';

export const LEVELS = [
  { value: 'none', label: 'None' },
  { value: 'read', label: 'View' },
  { value: 'read_write', label: 'Edit' },
  { value: 'read_write_delete', label: 'Full' },
];

export const SCOPES = [
  { value: 'own', label: 'Own' },
  { value: 'team', label: 'Team' },
  { value: 'team_and_subteams', label: '+ Sub-teams' },
  { value: 'all', label: 'Everyone' },
];

/**
 * One column template shared by the header, the group rows and the object rows.
 *
 * The two control columns are fixed widths rather than `auto`. With `auto` each
 * row sized itself to its own content, so the header labels sat over nothing in
 * particular and the controls stepped in and out as values changed.
 */
const GRID_COLS = 'minmax(200px, 1fr) 232px 300px 36px';

const LEVEL_HELP = {
  none: 'Hidden entirely',
  read: 'Can view but not change',
  read_write: 'Can view, create and edit',
  read_write_delete: 'Can also delete',
};

/** Ordered choices read better as a segmented control than a dropdown. */
function Segmented({ value, options, onChange, disabled, mixed, dense }) {
  return (
    <Box
      role="group"
      sx={{
        display: 'flex',
        width: '100%',
        p: '2px',
        borderRadius: '8px',
        backgroundColor: 'rgba(14,35,72,0.05)',
        opacity: disabled ? 0.45 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {options.map((option) => {
        const active = !mixed && option.value === value;
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            onClick={() => onChange(option.value)}
            title={LEVEL_HELP[option.value] ?? undefined}
            sx={{
              border: 0,
              flex: 1,
              textAlign: 'center',
              cursor: 'pointer',
              borderRadius: '6px',
              px: dense ? 0.5 : 0.75,
              py: dense ? 0.35 : 0.5,
              fontSize: dense ? 11.5 : 12.5,
              fontWeight: active ? 600 : 500,
              lineHeight: 1.6,
              whiteSpace: 'nowrap',
              transition: 'background-color .15s, color .15s',
              backgroundColor: active ? 'background.paper' : 'transparent',
              color: active ? 'info.main' : 'text.secondary',
              boxShadow: active ? '0 1px 2px rgba(14,35,72,.14)' : 'none',
              '&:hover': { color: active ? 'info.main' : 'text.primary' },
            }}
          >
            {option.label}
          </Box>
        );
      })}
      {mixed && (
        <Box
          sx={{
            alignSelf: 'center',
            px: 1,
            fontSize: 11.5,
            fontStyle: 'italic',
            color: 'text.disabled',
          }}
        >
          mixed
        </Box>
      )}
    </Box>
  );
}

function ObjectRow({ object, overridden, onChange, onReset, readOnly }) {
  const noAccess = object.grant_level === 'none';
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: GRID_COLS },
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 1.5,
        borderLeft: '2px solid',
        borderLeftColor: overridden ? 'info.main' : 'transparent',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      {/* Indent the label alone. Indenting the row would shift every column
          and break alignment with the group header above. */}
      <Box minWidth={0} pl={1.5}>
        <Typography variant="body2" noWrap>{object.label}</Typography>
        {!object.supports_scope && (
          <Typography variant="caption" color="text.secondary">
            Organisation-wide setting
          </Typography>
        )}
      </Box>
      <Segmented
        dense
        value={object.grant_level}
        options={LEVELS}
        disabled={readOnly}
        onChange={(v) => onChange({ grant_level: v })}
      />
      <Segmented
        dense
        value={object.record_scope}
        options={SCOPES}
        disabled={readOnly || noAccess || !object.supports_scope}
        onChange={(v) => onChange({ record_scope: v })}
      />
      <Box textAlign="right">
        {overridden && !readOnly && (
          <Tooltip title="Reset to the group setting">
            <IconButton size="small" onClick={onReset}>
              <RestartAlt sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

function GroupCard({ group, expanded, onToggle, readOnly, onChange, onGroupChange, onConfirm }) {
  const overrides = useMemo(() => {
    if (!group.rollup.is_mixed) return 0;
    return group.objects.filter(
      (o) =>
        o.grant_level !== group.rollup.grant_level ||
        o.record_scope !== group.rollup.record_scope
    ).length;
  }, [group]);

  const apply = (patch) => {
    if (group.rollup.is_mixed) {
      onConfirm({ group, patch, overrides });
      return;
    }
    onGroupChange(group, patch);
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px',
        backgroundColor: 'background.paper',
        overflow: 'hidden',
        transition: 'border-color .15s',
        '&:hover': { borderColor: 'info.main' },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: GRID_COLS },
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1.25,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <Box minWidth={0}>
            <Box display="flex" alignItems="center" gap={0.75}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {group.label}
              </Typography>
              {overrides > 0 && (
                <Chip
                  size="small"
                  label={`${overrides} custom`}
                  sx={{ height: 18, fontSize: 10.5, fontWeight: 600 }}
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {group.description}
            </Typography>
          </Box>
        </Box>

        <Segmented
          value={group.rollup.grant_level ?? 'none'}
          mixed={group.rollup.is_mixed}
          options={LEVELS}
          disabled={readOnly}
          onChange={(v) => apply({ grant_level: v })}
        />
        <Segmented
          value={group.rollup.record_scope ?? 'own'}
          mixed={group.rollup.is_mixed}
          options={SCOPES}
          disabled={readOnly || group.rollup.grant_level === 'none'}
          onChange={(v) => apply({ record_scope: v })}
        />

        <Tooltip title={expanded ? 'Hide individual records' : 'Set individual records'}>
          <IconButton size="small" onClick={onToggle}>
            {expanded ? <ExpandMore sx={{ fontSize: 18 }} /> : <TuneRounded sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', py: 0.5 }}>
          {group.objects.map((object) => (
            <ObjectRow
              key={object.api_name}
              object={object}
              readOnly={readOnly}
              overridden={
                group.rollup.is_mixed &&
                (object.grant_level !== group.rollup.grant_level ||
                  object.record_scope !== group.rollup.record_scope)
              }
              onChange={(patch) => onChange(object.api_name, patch)}
              onReset={() =>
                onChange(object.api_name, {
                  grant_level: group.rollup.grant_level ?? 'none',
                  record_scope: group.rollup.record_scope ?? 'own',
                })
              }
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function PermissionGrid({ groups, readOnly, onChange, onGroupChange }) {
  const [expanded, setExpanded] = useState({});
  const [confirm, setConfirm] = useState(null);

  return (
    <>
      {readOnly && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 1.25,
            mb: 2,
            borderRadius: '10px',
            backgroundColor: 'rgba(14,35,72,0.05)',
            color: 'text.secondary',
          }}
        >
          <LockOutlined sx={{ fontSize: 18 }} />
          <Typography variant="body2">
            Administrators always have full access. This grid is shown for
            reference and cannot be changed.
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: GRID_COLS },
          gap: 1.5,
          px: 1.5,
          pb: 0.75,
          // The group cards carry a 1px border; matching it here keeps the
          // header labels sitting exactly over their controls.
          border: '1px solid transparent',
          borderBottom: 0,
        }}
      >
        <Box />
        <Typography variant="caption" fontWeight={600} color="text.secondary"
          sx={{ letterSpacing: '.06em', textTransform: 'uppercase' }}>
          Can do
        </Typography>
        <Typography variant="caption" fontWeight={600} color="text.secondary"
          sx={{ letterSpacing: '.06em', textTransform: 'uppercase' }}>
          Whose records
        </Typography>
        <Box />
      </Box>

      <Box display="grid" gap={1}>
        {groups.map((group) => (
          <GroupCard
            key={group.code}
            group={group}
            readOnly={readOnly}
            expanded={Boolean(expanded[group.code])}
            onToggle={() =>
              setExpanded((prev) => ({ ...prev, [group.code]: !prev[group.code] }))
            }
            onChange={onChange}
            onGroupChange={onGroupChange}
            onConfirm={setConfirm}
          />
        ))}
      </Box>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Replace individual settings?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.overrides} record type(s) in {confirm?.group.label} are set
            individually. Applying a group setting will overwrite them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="info"
            onClick={() => {
              onGroupChange(confirm.group, confirm.patch);
              setConfirm(null);
            }}
          >
            Apply to all
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
